import Service, { service } from '@ember/service';
import type TourStorageService from './tour-storage';
import { driver, type Driver, type Config, type DriveStep, type PopoverDOM } from 'driver.js';

/**
 * Represents a tour configuration that could come from a database.
 */
export interface TourConfig {
  id: string;
  name: string;
  steps: DriveStep[];
  driverOptions?: Partial<Config>;
}

/**
 * Service for managing guided tours using driver.js.
 * Designed with database-driven tours in mind - currently uses static configs,
 * but structured to easily switch to API-fetched tour definitions.
 *
 * @memberof services
 */
export default class TourManagerService extends Service {
  @service declare tourStorage: TourStorageService;

  private activeDriver: Driver | null = null;

  /**
   * When true, the driver is being destroyed programmatically (not by the user),
   * so onDestroyed should NOT mark the tour as completed.
   */
  private isProgrammaticDestroy = false;

  /**
   * In the future, this would fetch tour configs from an API endpoint.
   * For now, we maintain a static registry that can be easily migrated.
   */
  private tourRegistry: Map<string, TourConfig> = new Map();

  /**
   * Register a tour configuration.
   * This simulates what would eventually be stored in a database.
   */
  registerTour(config: TourConfig) {
    this.tourRegistry.set(config.id, config);
  }

  /**
   * Get a tour configuration by ID.
   * In the future, this would be an API call.
   */
  getTour(tourId: string): TourConfig | undefined {
    return this.tourRegistry.get(tourId);
  }

  /**
   * Start a tour if it hasn't been completed yet.
   */
  async startTour(tourId: string, force = false): Promise<boolean> {
    // Check if tour has already been completed
    if (!force && this.tourStorage.isTourCompleted(tourId)) {
      return false;
    }

    // Get tour configuration
    const tourConfig = this.getTour(tourId);
    if (!tourConfig) {
      console.warn(`Tour not found: ${tourId}`);
      return false;
    }

    // Clean up any existing driver instance
    this.cleanup();

    // Create and configure the driver
    this.activeDriver = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      popoverClass: 'tio-popover',
      ...tourConfig.driverOptions,
      // × icon = dismiss and mark completed so it doesn't reappear
      onCloseClick: (element, step, opts) => {
        tourConfig.driverOptions?.onCloseClick?.(element, step, opts);
        this.activeDriver?.destroy();
      },
      // Add primary class to the Next/Done button for CSS styling
      onPopoverRender: (popover: PopoverDOM, opts) => {
        popover.nextButton?.classList.add('tio-btn-primary');
        tourConfig.driverOptions?.onPopoverRender?.(popover, opts);
      },
      onDestroyed: (element, step, options) => {
        if (!this.isProgrammaticDestroy) {
          this.tourStorage.markTourCompleted(tourId);
        }
        this.activeDriver = null;
        tourConfig.driverOptions?.onDestroyed?.(element, step, options);
      },
      steps: tourConfig.steps,
    });

    // Start the tour
    this.activeDriver?.drive();
    return true;
  }

  /**
   * Stop the currently active tour.
   */
  stopTour() {
    if (this.activeDriver) {
      this.isProgrammaticDestroy = true;
      try {
        this.activeDriver.destroy();
      } finally {
        this.isProgrammaticDestroy = false;
        this.activeDriver = null;
      }
    }
  }

  /**
   * Clean up any active tours.
   */
  cleanup() {
    this.stopTour();
  }

  /**
   * Check if a tour should be shown (hasn't been completed).
   */
  shouldShowTour(tourId: string): boolean {
    return !this.tourStorage.isTourCompleted(tourId);
  }

  /**
   * Get all registered tour IDs.
   * In the future, this could fetch available tours for the current user.
   */
  getAvailableTours(): string[] {
    return Array.from(this.tourRegistry.keys());
  }
}

declare module '@ember/service' {
  interface Registry {
    'tour-manager': TourManagerService;
  }
}
